import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowRight, Phone, Mail, MapPin, CheckCircle2, AlertCircle,
  Loader2, MessageSquare, ShieldCheck, ThumbsUp, Truck, Clock,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import { useSeo } from '../lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const SUBJECT_OPTIONS = [
  'Request a Quote',
  'Windshield Replacement',
  'ADAS Calibration',
  'Mobile Service',
  'Insurance Claim',
  'General Inquiry',
  'Other',
];

const INITIAL = {
  first_name: '', last_name: '', email: '', phone: '', subject: '', message: '',
};

// -------- Scroll fade helper (same feel as WhyChooseUs) --------
function useOnScreen(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.2 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >{children}</div>
  );
}

// -------- Field --------
function inputCls(hasError) {
  return `w-full h-12 px-4 rounded-lg border ${hasError ? 'border-rose-400 focus:border-rose-500 focus:ring-rose-200' : 'border-slate-200 focus:border-[#0D5BD7] focus:ring-blue-100'} bg-white text-[14px] text-[#111827] placeholder:text-slate-400 focus:outline-none focus:ring-4 transition-colors`;
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-[#111827]">
        {label} {required && <span className="text-[#0D5BD7]">*</span>}
      </label>
      <div className="mt-2">{children}</div>
      {error && (
        <div className="mt-1.5 flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ================== PAGE ==================
export default function Contact() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useSeo({
    title: 'Contact A+ Auto Glass Ottawa — Get a Free Windshield Quote',
    description:
      'Contact A+ Auto Glass for a free quote on windshield replacement, rock chip repair or ADAS calibration in Ottawa. Call 343-558-8820 or send us a message. Mon–Fri 8am–5pm.',
    path: '/contact',
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'First name is required';
    if (!form.last_name.trim()) e.last_name = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (!form.phone.trim()) e.phone = 'Phone number is required';
    else if (!/^[\d\s()+\-.]{7,}$/.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    if (!form.subject) e.subject = 'Please choose a subject';
    if (!form.message.trim()) e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/public/contact-messages`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject,
        message: form.message.trim(),
      });
      setSuccess(true);
      window.scrollTo({ top: document.getElementById('contact-form')?.offsetTop - 100 || 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-white text-[#111827]">
      <Navbar />

      {/* HERO (desktop only) — matches Services / Why Choose Us / Locations */}
      <section className="hidden lg:block relative overflow-hidden bg-[#0a1220] text-white">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0D5BD7]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14">
          <h1 className="font-black leading-[1.05] tracking-[-0.015em] text-[32px] md:text-[42px] lg:text-[48px] max-w-[680px]">
            We&rsquo;d Love To Hear From You
          </h1>
          <p className="mt-4 text-[15px] md:text-[16.5px] text-white/85 max-w-[600px] leading-[1.65]">
            Have a question, need a quote, or want to schedule a service? Our team is ready to
            help &mdash; reach out today.
          </p>
        </div>
      </section>

      {/* GET IN TOUCH */}
      <section className="bg-white pt-16 md:pt-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="text-center max-w-2xl mx-auto">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">GET IN TOUCH</div>
            <h2 className="mt-3 text-[32px] md:text-[46px] font-black text-[#111827] tracking-[-0.01em] leading-[1.05]">
              We&rsquo;re Here To Help
            </h2>
            <p className="mt-5 text-[15px] text-[#5F6773] leading-[1.7]">
              Contact us by phone, email, visit one of our locations, or send us a message below.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Reveal delay={0}>
              <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] p-8 hover:shadow-[0_18px_40px_rgba(13,91,215,0.10)] hover:-translate-y-1 transition-all duration-300 text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center shadow-md">
                  <Phone className="w-7 h-7" strokeWidth={1.7} />
                </div>
                <div className="mt-5 text-[13px] font-bold tracking-[0.16em] text-[#111827]">PHONE</div>
                <a href="tel:3435588820" className="mt-2 block text-[22px] font-black text-[#0D5BD7] hover:underline">343-558-8820</a>
                <div className="mt-4 text-[13px] text-[#5F6773] leading-[1.7]">
                  <div>Monday &ndash; Friday</div>
                  <div>8:00 AM &ndash; 5:00 PM</div>
                  <div className="mt-1">Saturday &amp; Sunday: <span className="text-[#111827] font-semibold">Closed</span></div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] p-8 hover:shadow-[0_18px_40px_rgba(13,91,215,0.10)] hover:-translate-y-1 transition-all duration-300 text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center shadow-md">
                  <Mail className="w-7 h-7" strokeWidth={1.7} />
                </div>
                <div className="mt-5 text-[13px] font-bold tracking-[0.16em] text-[#111827]">EMAIL</div>
                <a href="mailto:info@aplusautoglass.ca" className="mt-2 block text-[17px] font-bold text-[#0D5BD7] hover:underline break-all">info@aplusautoglass.ca</a>
                <p className="mt-4 text-[13px] text-[#5F6773] leading-[1.7]">
                  We respond to all inquiries within one business day.
                </p>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="h-full bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] p-8 hover:shadow-[0_18px_40px_rgba(13,91,215,0.10)] hover:-translate-y-1 transition-all duration-300 text-center flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#0D5BD7] text-white flex items-center justify-center shadow-md">
                  <MapPin className="w-7 h-7" strokeWidth={1.7} />
                </div>
                <div className="mt-5 text-[13px] font-bold tracking-[0.16em] text-[#111827]">LOCATIONS</div>
                <p className="mt-3 text-[14px] text-[#5F6773] leading-[1.7]">
                  Proudly serving Ottawa and the surrounding communities.
                </p>
                <a
                  href="#locations"
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-full border border-[#0D5BD7] text-[#0D5BD7] hover:bg-[#0D5BD7] hover:text-white text-[11px] font-bold tracking-[0.2em] transition-colors"
                >
                  VIEW LOCATIONS <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FORM + INFO CARD */}
      <section id="contact-form" className="bg-white py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Form card */}
          <Reveal className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(15,23,42,0.06)] p-8 md:p-10">
              <div className="text-[13px] font-bold tracking-[0.18em] text-[#111827]">SEND US A MESSAGE</div>

              {success ? (
                <div className="mt-8 text-center py-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center animate-[pulse_2s_ease-in-out_infinite]">
                    <CheckCircle2 className="w-9 h-9" />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-[#111827]">Thank you!</h3>
                  <p className="mt-2 text-[15px] text-[#5F6773] max-w-md mx-auto leading-[1.7]">
                    We&rsquo;ve received your message and a member of our team will contact you shortly. A confirmation email is on its way to your inbox.
                  </p>
                  <button
                    onClick={() => { setSuccess(false); setForm(INITIAL); }}
                    className="mt-6 inline-flex items-center h-11 px-6 rounded-full border border-slate-200 text-[#111827] hover:bg-slate-50 text-[12px] font-bold tracking-[0.2em]"
                  >
                    SEND ANOTHER MESSAGE
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="mt-6 space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="First Name" required error={errors.first_name}>
                      <input type="text" value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} className={inputCls(!!errors.first_name)} autoComplete="given-name" />
                    </Field>
                    <Field label="Last Name" required error={errors.last_name}>
                      <input type="text" value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} className={inputCls(!!errors.last_name)} autoComplete="family-name" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Email Address" required error={errors.email}>
                      <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="you@email.com" className={inputCls(!!errors.email)} autoComplete="email" />
                    </Field>
                    <Field label="Phone Number" required error={errors.phone}>
                      <input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="343-555-0000" className={inputCls(!!errors.phone)} autoComplete="tel" />
                    </Field>
                  </div>
                  <Field label="Subject" required error={errors.subject}>
                    <select value={form.subject} onChange={(e) => setField('subject', e.target.value)} className={inputCls(!!errors.subject)}>
                      <option value="">Choose a subject…</option>
                      {SUBJECT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="Message" required error={errors.message}>
                    <textarea rows={5} value={form.message} onChange={(e) => setField('message', e.target.value)} placeholder="Tell us how we can help…" className={`${inputCls(!!errors.message)} h-auto py-3 resize-y min-h-[120px]`} />
                  </Field>

                  {submitError && (
                    <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700 flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="group w-full sm:w-auto inline-flex items-center justify-center h-[54px] px-8 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] disabled:opacity-70 disabled:cursor-not-allowed text-white text-[12px] font-bold tracking-[0.22em] transition-all shadow-md hover:shadow-lg hover:-translate-y-[1px]"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> SENDING…</>
                    ) : (
                      <>SEND MESSAGE <ArrowRight className="w-4 h-4 ml-4 transition-transform group-hover:translate-x-1" /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* Info card */}
          <Reveal delay={150}>
            <div className="h-full bg-[#F8F9FB] rounded-2xl border border-slate-100 p-8 md:p-10">
              <div className="text-[13px] font-bold tracking-[0.18em] text-[#0D5BD7]">WE RESPOND FAST</div>
              <h3 className="mt-3 text-[22px] font-black text-[#111827] leading-tight">
                Whether you need a quote, a mobile visit, or advice — we&rsquo;re <span className="text-[#0D5BD7]">just a message away.</span>
              </h3>
              <div className="mt-7 space-y-6">
                <InfoRow
                  icon={Clock}
                  title="Quick Response"
                  desc="We typically reply within one business hour during operating hours."
                />
                <InfoRow
                  icon={ShieldCheck}
                  title="Expert Advice"
                  desc="Speak directly with experienced auto glass and ADAS specialists."
                />
                <InfoRow
                  icon={ThumbsUp}
                  title="Customer First"
                  desc="Friendly, honest service with transparent communication every step."
                />
              </div>
            </div>
          </Reveal>
        </div>

        {/* Emergency CTA */}
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-6">
          <Reveal>
            <div className="bg-[#F8F9FB] border border-slate-100 rounded-2xl px-6 md:px-10 py-6 md:py-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
              <div className="flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#0D5BD7]/10 text-[#0D5BD7] flex items-center justify-center shrink-0">
                  <Truck className="w-7 h-7" strokeWidth={1.6} />
                </div>
                <div>
                  <div className="text-[17px] font-bold text-[#111827]">Need Immediate Assistance?</div>
                  <div className="mt-1 text-[14px] text-[#5F6773] leading-[1.6] max-w-xl">
                    Book our mobile windshield service and we&rsquo;ll come directly to your home or workplace. Fast, convenient, and professional.
                  </div>
                </div>
              </div>
              <button
                onClick={() => setQuoteOpen(true)}
                className="group inline-flex items-center justify-center w-full md:w-auto h-[52px] px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[12px] font-bold tracking-[0.22em] transition-all shadow-md hover:shadow-lg hover:-translate-y-[1px] active:scale-[0.98] shrink-0"
              >
                BOOK MOBILE SERVICE <ArrowRight className="w-4 h-4 ml-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />

      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}

function InfoRow({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-white border border-slate-200 text-[#0D5BD7] flex items-center justify-center shrink-0 shadow-sm">
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-[15px] font-bold text-[#111827]">{title}</div>
        <div className="mt-1 text-[13.5px] text-[#5F6773] leading-[1.65]">{desc}</div>
      </div>
    </div>
  );
}
